import { PrimaryButton } from "@mnk/frontend/components/ui/button/PrimaryButton";
import { trpc } from "@mnk/frontend/lib/trpc";
import type { GetItem } from "@mnk/shared/models/item";
import { useState } from "react";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  item: GetItem;
  mutateItems: () => Promise<void>;
}

export const ChangeNameModal = ({ isOpen, onClose, item, mutateItems }: Props) => {
  const [name, setName] = useState(item.name);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const { mutate: changeFolderName, isLoading: isFolderLoading } = trpc.changeFolderName.useMutation({
    onSuccess: () => {
      mutateItems()
        .then(() => {
          setIsProcessing(false);
          onClose();
        })
        .catch((error) => {
          console.error("Failed to mutate items:", error);
          setError("アイテムの更新に失敗しました");
          setIsProcessing(false);
        });
    },
    onError: () => {
      setError("フォルダ名の変更に失敗しました");
      setIsProcessing(false);
    },
  });

  const { mutate: changeProjectName, isLoading: isProjectLoading } = trpc.changeProjectName.useMutation({
    onSuccess: () => {
      mutateItems()
        .then(() => {
          setIsProcessing(false);
          onClose();
        })
        .catch((error) => {
          console.error("Failed to mutate items:", error);
          setError("アイテムの更新に失敗しました");
          setIsProcessing(false);
        });
    },
    onError: () => {
      setError("プロジェクト名の変更に失敗しました");
      setIsProcessing(false);
    },
  });

  const handleSubmit = async () => {
    setIsProcessing(true);
    if (item.itemType === "folder") {
      changeFolderName({
        itemId: item.id,
        name,
      });
    } else {
      changeProjectName({
        itemId: item.id,
        name,
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/40 flex justify-center items-center z-50"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          onClose();
        }
      }}
    >
      <div className="w-full h-full flex justify-center items-center">
        <div
          className="bg-white rounded-lg shadow-lg w-96 flex flex-col"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              onClose();
            }
          }}
        >
          <div className="bg-gray-100 rounded-t-lg flex justify-between items-center p-2 relative h-10 flex-shrink-0">
            <h2 className="text-center text-md font-bold mx-auto">
              {item.itemType === "folder" ? "フォルダ名の変更" : "プロジェクト名の変更"}
            </h2>
          </div>
          <div className="p-4 flex flex-col gap-2 rounded-b-lg">
            <div className="flex flex-col gap-2">
              <label htmlFor="name" className="text-sm text-gray-500">
                名前
              </label>
              <textarea
                id="name"
                className="border border-gray-300 rounded-md p-2 resize-none"
                rows={3}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            {error && <p className="text-sm text-red-500 text-center">{error}</p>}

            <PrimaryButton className="w-full" onClick={handleSubmit} disabled={isFolderLoading || isProjectLoading}>
              {isProcessing ? "変更中..." : "変更"}
            </PrimaryButton>
          </div>
        </div>
      </div>
    </div>
  );
};
